import { Outlet, Navigate, NavLink } from "react-router-dom";
import { useEffect } from "react";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { Loader2, LayoutDashboard, Flag, Users, CreditCard, Settings, Home, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { BrandLogo } from "@/components/brand/BrandLogo";

export function AdminLayout() {
  const { isAdmin, isLoading, checkAdminStatus } = useSuperAdmin();
  const { signOut } = useAuth();

  useEffect(() => {
    checkAdminStatus();
  }, []); // Remove checkAdminStatus from dependencies to avoid infinite loop if it's not stable

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    console.log("Access denied: User is not an admin. Redirecting to home.");
    // Changed to /dashboard because / might redirect back to /dashboard for logged in users
    // creating a potential loop if dashboard redirects to admin? No, dashboard is safe.
    return <Navigate to="/dashboard" replace />;
  }

  const navItems = [
    { label: "Dashboard", href: "/admin", icon: LayoutDashboard },
    { label: "Usuários", href: "/admin/users", icon: Users },
    { label: "Feature Flags", href: "/admin/flags", icon: Flag },
    { label: "Assinaturas", href: "/admin/subscriptions", icon: CreditCard },
    { label: "Configurações", href: "/admin/settings", icon: Settings },
  ];

  return (
    <div className="flex min-h-screen bg-[#F8F7FF]">
      <aside className="flex w-64 flex-col border-r border-[#221E4F] bg-[#0C0B18] text-white">
        <div className="flex h-20 items-center justify-between border-b border-white/10 px-6">
          <BrandLogo size="sm" inverted textClassName="text-white" />
          <span className="rounded-full bg-[#C4A45A] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#26215C]">
            Admin
          </span>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.href === "/admin"}
              className={({ isActive }) => cn(
                "flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors",
                isActive 
                  ? "bg-[#16142A] text-white font-medium shadow-[inset_0_0_0_1px_rgba(107,99,204,0.28)]" 
                  : "text-white/58 hover:bg-white/6 hover:text-white"
              )}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/10 p-4">
          <NavLink
            to="/"
            className="mb-1 flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-white/58 hover:bg-white/6 hover:text-white"
          >
            <Home className="h-4 w-4" />
            Voltar ao App
          </NavLink>
          <button
            onClick={() => signOut()}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm text-[#E8C97A] hover:bg-[#C4A45A]/10 hover:text-[#E8C97A]"
          >
            <LogOut className="h-4 w-4" />
            Sair
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-[#E7E3FB] bg-white/85 px-8 backdrop-blur-md">
          <h1 className="text-xl font-semibold text-[#26215C]">Painel Administrativo</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#8B89A8]">Logado como Super Admin</span>
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#4A42A8] text-xs font-bold text-white">
              SA
            </div>
          </div>
        </header>
        <div className="mx-auto max-w-7xl p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
